/// <reference path="../../types/window.d.ts" />

type GA4QueueTarget = {
  Cookiebot?: {
    consent?: unknown;
  };
  dataLayer?: Array<Record<string, unknown> | unknown[]>;
  gtag?: Window["gtag"];
};

type QueueGA4EventOptions = {
  eventName: string;
  eventData: Record<string, unknown>;
  hasRequiredConsent: boolean;
  isDevelopment?: boolean;
  targetWindow?: GA4QueueTarget | null;
  warn?: (message: string) => void;
};

const GA4_MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]+$/;
const configuredGA4MeasurementId =
  process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_TAG_ID?.trim() ?? "";

/** Validated GA4 destination configured by the deployment. */
export const GA4_MEASUREMENT_ID = GA4_MEASUREMENT_ID_PATTERN.test(
  configuredGA4MeasurementId,
)
  ? configuredGA4MeasurementId
  : null;

export const queueGA4Event = ({
  eventName,
  eventData,
  hasRequiredConsent,
  isDevelopment = false,
  targetWindow,
  warn = console.warn,
}: QueueGA4EventOptions): boolean => {
  if (!targetWindow || !GA4_MEASUREMENT_ID) {
    return false;
  }

  targetWindow.dataLayer = targetWindow.dataLayer || [];

  if (!hasRequiredConsent) {
    if (isDevelopment && targetWindow.Cookiebot?.consent) {
      warn(
        `GA4 ${eventName} event not pushed to GTM because required Cookiebot consent is denied.`,
      );
    }
    return false;
  }

  if (typeof targetWindow.gtag !== "function") {
    if (isDevelopment) {
      warn(`GA4 ${eventName} event not sent because gtag is unavailable.`);
    }
    return false;
  }

  if (
    isDevelopment &&
    Object.prototype.hasOwnProperty.call(eventData, "send_to")
  ) {
    warn(
      `GA4 ${eventName} event ignored eventData.send_to and used the canonical destination instead.`,
    );
  }

  // The live GTM container configures both Google Ads and GA4. Explicitly
  // select GA4 so custom events are not left without a destination group.
  targetWindow.gtag("event", eventName, {
    ...eventData,
    send_to: GA4_MEASUREMENT_ID,
  });
  return true;
};
